import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

// Role-aware how-to guide. The viewer's own role opens expanded; the rest are
// collapsible so admins can see every workflow. Content is static — update it
// here when workflows change.

const SECTIONS = [
  {
    roles: ['staff'],
    title: 'Staff — Conducting Tours',
    body: [
      ['Starting a tour', 'Go to New Tour and pick your facility. Facilities marked ⛔ are unavailable — either a tour is already in progress there, or one was completed within the last 7 days (the label tells you which, and when the next tour can begin).'],
      ['Completing a tour', 'Tours are expected to be completed the same day they are started. Answer every question; anything marked non-compliant needs a note describing what you found.'],
      ['Drafts', 'If you must stop mid-tour, your progress saves as a draft — resume it from the tour list. A draft older than a day shows a ⚠ stale badge, and an open draft blocks new tours at that facility for up to 7 days, so submit or delete drafts promptly.'],
      ['After you submit', 'The facility\'s building admin is notified automatically. If your tour found issues, maintenance and environmental staff covering the facility are alerted too. You\'re done — follow-up happens at the building level.'],
    ],
  },
  {
    roles: ['building_admin'],
    title: 'Building Admin — Working Tours',
    body: [
      ['When a tour is submitted', 'You get an email listing any non-compliant issues. Open the tour and click Acknowledge Tour — this marks the tour as being worked and stops the reminder emails. Acknowledging is YOUR action: regional managers intentionally cannot do it.'],
      ['Reminders and escalation', 'Unacknowledged tours generate reminder emails every 8 hours. At 48 hours the tour escalates to your regional manager(s); at 72 hours to executive staff. Acknowledge promptly, even before the work is finished.'],
      ['Working issues', 'On the tour page, update each issue\'s status and add admin notes as work progresses. Maintenance and environmental staff record their own actions and sign off on their portions — you\'ll see their trail on each issue.'],
      ['Long-term items (Defer)', 'Some findings can\'t be fixed in normal timeframes — pot holes awaiting CAPEX, a roof out for contractor bids. Use Defer on the issue with a reason and target date: the tour can then resolve without it, future tours re-flagging the same item inherit the deferral automatically (no repeat alerts), and everything deferred is tracked on the Reports → Deferred Issues tab until it\'s reactivated and fixed.'],
      ['Resolving', 'When every issue is addressed, resolve them and the tour. Resolved tours stop all reminder/escalation activity.'],
      ['Exports', 'Any submitted tour can be exported as a PDF report or Excel from buttons in the tour header. The Reports page (Insights section) has summaries, issue detail, and compliance trends for your facility.'],
    ],
  },
  {
    roles: ['maintenance', 'environmental'],
    title: 'Maintenance / Environmental — Trade Work',
    body: [
      ['Alerts', 'When a tour at a facility you cover is submitted with issues, you get an email. Your coverage (which facilities or regions) is assigned by the administrator.'],
      ['Recording work', 'Open the tour, find the issues relevant to your trade, mark your action complete with a comment describing what was done.'],
      ['Sign-off', 'Once all your trade\'s work on a tour is done, add your sign-off on the tour. Your name and time are recorded and appear on reports.'],
    ],
  },
  {
    roles: ['regional_manager'],
    title: 'Regional Manager — Oversight',
    body: [
      ['Your scope', 'You see tours, reports, and dashboards for every facility in your region(s). Regions are assigned on your user profile by the administrator.'],
      ['Escalations', 'If a submitted tour goes 48 hours without the building admin acknowledging it, you get an escalation email. Follow up with the building admin — acknowledging is a building-level action, so you\'ll see a note (not a button) on unacknowledged tours.'],
      ['Tour limits', 'Facilities are limited to one tour per week. You can override this: blocked facilities stay selectable for you in New Tour, with a confirmation asking you to confirm the override.'],
      ['Reports', 'The Reports page (Insights section) covers your region — tour summaries, issue detail with resolution trails, and compliance trends, all exportable to Excel/CSV.'],
    ],
  },
  {
    roles: ['executive_staff'],
    title: 'Executive Staff — Company View',
    body: [
      ['Your scope', 'You see all facilities and regions across the company.'],
      ['Escalations', 'Tours unacknowledged for 72 hours escalate to all executive staff by email.'],
      ['All tour notifications', 'Optionally, the administrator can enable "Receive all tour notifications" on your profile — you\'ll then get the submission email for every tour at every facility.'],
      ['Reports', 'The Reports page includes company-wide compliance trends by month and facility, with Excel/CSV export.'],
    ],
  },
  {
    roles: ['super_admin'],
    title: 'Super Admin — Administration',
    body: [
      ['Admin Settings', 'Manage users (roles, facilities, regions, disable accounts, email changes, notification flags), facilities, and regions. Regional managers are assigned by checking regions on the USER\'s profile — a region can have any number of managers, and all receive escalations.'],
      ['Corrections', 'You can un-acknowledge a tour that was acknowledged by mistake (button on the tour page) — it returns to Pending Review and the reminder clock resumes from the original submission. You can also change a tour\'s facility, delete tours, and start tours anywhere (weekly limits don\'t apply to you).'],
      ['Email system', 'Templates are editable under Email Templates. The Escalation Chain page shows exactly who gets notified for each facility at each stage — amber warnings flag coverage gaps (facilities without admins, regions without managers).'],
      ['Trade coverage', 'Maintenance/environmental staff are mapped to facilities or whole regions in Admin Settings; that mapping drives their alerts.'],
    ],
  },
];

export default function Help() {
  const { user } = useAuth();
  const myIndex = SECTIONS.findIndex(s => s.roles.includes(user?.role));
  const [open, setOpen] = useState(() => new Set([myIndex >= 0 ? myIndex : 0]));

  const toggle = (i) => setOpen(prev => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  // Show the viewer's own section first
  const ordered = SECTIONS.map((s, i) => ({ ...s, i })).sort((a, b) =>
    (a.i === myIndex ? -1 : b.i === myIndex ? 1 : a.i - b.i));

  return (
    <>
      <div className="page-header">
        <div>
          <h2>How Pillar Works</h2>
          <p>Each role has its own workflow — yours is expanded below.</p>
        </div>
      </div>
      <div className="page-body">
        {ordered.map(section => (
          <div key={section.i} className="card" style={{ marginBottom: 12, padding: 0, overflow: 'hidden' }}>
            <button
              onClick={() => toggle(section.i)}
              style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
                padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-heading)' }}>
                {section.title}
                {section.i === myIndex && <span className="badge badge-acknowledged" style={{ marginLeft: 10 }}>Your role</span>}
              </span>
              <span style={{ color: 'var(--text-secondary)' }}>{open.has(section.i) ? '▾' : '▸'}</span>
            </button>
            {open.has(section.i) && (
              <div style={{ padding: '0 18px 16px' }}>
                {section.body.map(([heading, text]) => (
                  <div key={heading} style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text-heading)', marginBottom: 2 }}>{heading}</div>
                    <div style={{ fontSize: 13.5, color: 'var(--text-primary)', lineHeight: 1.55 }}>{text}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
